import React from 'react';
import ImageGallery from 'react-image-gallery';
import LazyImage from '../../pages/component/LazyImage';
import { extractYouTubeId, youTubeThumbnailUrl } from '../youtube';

const GalleryRenderer = ({ comp }) => {
    const [aspectRatio, setAspectRatio] = React.useState(null);
    const [isFullscreen, setIsFullscreen] = React.useState(false);

    React.useEffect(() => {
        if (comp.type !== 'gallery' || !comp.images || comp.images.length === 0) return;
        const firstItem = comp.images[0];
        if (firstItem.type === 'youtube') {
            // Can't preload a video's natural size the way we do for images; embeds are
            // standard widescreen so this is a safe default rather than leaving it blank.
            setAspectRatio(16 / 9);
            return;
        }
        const img = new Image();
        img.onload = () => {
            setAspectRatio(img.width / img.height);
        };
        img.src = firstItem.src;
    }, [comp]);

    if (comp.type === 'gallery') {
        if (!aspectRatio) {
            return (
                <div className="img-spinner" style={{ width: '100%', aspectRatio: '4 / 3' }} />
            );
        }
        const images = (comp.images || []).map(img => {
            const isVideo = img.type === 'youtube';
            const videoId = isVideo ? extractYouTubeId(img.src) : null;
            const thumbnail = isVideo
                ? (youTubeThumbnailUrl(img.src) || '')
                : img.src.replace(/(\.[^.]+)$/, '_thumb$1');
            // Per-item title is optional; fall back to the artwork/page title so images
            // never end up with an empty alt attribute.
            const alt = img.title || comp.altFallback || '';
            return {
                isVideo,
                videoId,
                original: isVideo ? (thumbnail || img.src) : img.src,
                thumbnail,
                description: comp.showImageTitles === false ? '' : (img.title || ''),
                title: img.title,
                alt,
                originalAlt: alt,
                thumbnailAlt: alt,
            };
        });

        const imgStyle = { aspectRatio, width: '100%', height: 'auto', objectFit: 'cover', display: 'block' };

        const renderItem = (item) => {
            if (item.isVideo) {
                if (!item.videoId) return null;
                return (
                    <div className="image-gallery-image">
                        <div style={{ width: '100%', aspectRatio }}>
                            <iframe
                                src={`https://www.youtube.com/embed/${item.videoId}?rel=0`}
                                title={item.alt || 'YouTube video'}
                                style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                        {item.description && (
                            <span className="image-gallery-description">
                                {item.description}
                            </span>
                        )}
                    </div>
                );
            }

            const src = isFullscreen ? item.original : item.thumbnail;
            return (
                <div className="image-gallery-image">
                    <LazyImage
                        src={src}
                        alt={item.alt}
                        imgStyle={imgStyle}
                    />
                    {item.description && (
                        <span className="image-gallery-description">
                            {item.description}
                        </span>
                    )}
                </div>
            );
        };

        return (
            <div style={{ width: '100%' }}>
                <style>{`
                    .image-gallery-fullscreen-button {
                        top: 0;
                        right: 0;
                        bottom: auto;
                    }
                    .image-gallery-fullscreen-button .image-gallery-svg {
                        height: 28px;
                        width: 28px;
                    }
                    /* The library sets touch-action: none on the slides container so it can
                       capture horizontal swipes for navigation. That also blocks vertical
                       page scrolling while a finger/trackpad touch starts over the gallery.
                       Allow vertical panning (and pinch-zoom) through, matching the
                       .image-gallery-swipe wrapper right next to it, so the page still scrolls
                       normally when hovering/touching the gallery. */
                    .image-gallery-slides {
                        touch-action: pan-y pinch-zoom;
                        overscroll-behavior: auto;
                    }
                `}</style>
                <ImageGallery
                    items={images}
                    showNav={comp.showNav ?? false}
                    autoPlay={comp.autoPlay ?? true}
                    slideInterval={(comp.slideIntervalSeconds ?? 7) * 1000}
                    showFullscreenButton={comp.showFullScreenButton ?? true}
                    showPlayButton={false}
                    showThumbnails={comp.showThumbnails ?? false}
                    additionalClass="fixed-height-gallery"
                    renderItem={renderItem}
                    onScreenChange={(full) => setIsFullscreen(full)}
                />
            </div>
        );
    }
    return null;
};

export default GalleryRenderer;
